import {
  IMAGING_AND_TESTS_AVAILABLE_IMAGES,
  IMAGING_AND_TESTS_FINDINGS,
  IMAGING_AND_TESTS_HISTORY,
  IMAGING_AND_TESTS_RECOMMENDATIONS,
  IMAGING_AND_TESTS_REPORTED_DATE,
  IMAGING_AND_TESTS_TECHNIQUE,
  IMAGING_AND_TESTS_TEST_NAME,
  LAB_REPORT_TEST_NAME,
} from '@constants/extension-codes';
import { IMAGING_TEST_DIAGNOSTIC_REPORT_TYPE_MAP } from '@constants/option-types/imaging-test';
import { LAB_REPORT_NAME_MAP } from '@constants/option-types/lab';

import type { LabObservationValues } from '@models/lab-result';

import { formatFhirDate, getFhirDate } from '@utils/fhir';

import type {
  DiagnosticReportInput,
  DiagnosticReportModel,
  Reference,
} from 'generated/types';

import { EncounterRecord } from './encounter';
import { FhirResource, type BaseFhirFormValues } from './fhir-resource';
import { ImagingStudyRecord } from './imaging-study';
import { LabResultObservationRecord } from './lab-result';

export type DiagnosticReportValues = Pick<
  DiagnosticReportRecord,
  | 'city'
  | 'code'
  | 'country'
  | 'details'
  | 'facilityName'
  | 'findings'
  | 'history'
  | 'practitioner'
  | 'recommendations'
  | 'reportedDate'
  | 'selfReported'
  | 'technique'
  | 'testDate'
  | 'typeName'
> & {
  // Imaging and Tests: Test Name
  title?: string;
  imagesAvailable?: boolean;
  // Lab result: observations
  results?: LabObservationValues[];
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & DiagnosticReportValues;

type DiagnosticReportType = DiagnosticReportModel;

export class DiagnosticReportRecord extends FhirResource {
  protected model: DiagnosticReportType;

  public readonly encounter: EncounterRecord | null;

  public constructor(model: DiagnosticReportType) {
    super(model);
    this.model = model;
    this.encounter = model.encounterResource
      ? new EncounterRecord(model.encounterResource)
      : null;
  }

  public get facilityName() {
    return this.encounter?.facility;
  }

  public get testLocation() {
    return this.encounter?.address;
  }

  public get city() {
    return this.encounter?.city;
  }

  public get country() {
    return this.encounter?.country;
  }

  public get practitioner() {
    const [hcp] = this.model.performer ?? [];
    return this.encounter?.practitioner ?? hcp?.display;
  }

  public set practitioner(value: string | null | undefined) {
    this.model = {
      ...this.model,
      performer: value
        ? [
            {
              display: value,
            },
          ]
        : [],
    };
  }

  // 'imaging-and-tests' or 'laboratory'
  public get category() {
    const [{ coding }] = this.model.category ?? [];
    const [{ code }] = coding ?? [];
    return code;
  }

  public get typeName() {
    const { text } = this.model.code;
    return text?.startsWith('Labs') ? text.replace('Labs', '') : text;
  }

  // Changed only for imaging and tests
  public set typeName(value: string | null | undefined) {
    this.model = {
      ...this.model,
      ...(this.category === 'imaging-and-tests' && {
        code: value
          ? {
              coding: [
                {
                  code: IMAGING_TEST_DIAGNOSTIC_REPORT_TYPE_MAP[value],
                  display: value,
                  system: DiagnosticReportRecord.VITALL_SYSTEM,
                },
              ],
              text: value,
            }
          : {},
      }),
    };
  }

  public get vitallCode() {
    const { coding } = this.model.code;
    return coding?.find(
      ({ system }) => system === DiagnosticReportRecord.VITALL_SYSTEM
    )?.code;
  }

  public get code() {
    const { coding } = this.model.code;
    return (coding ?? [])
      .map(({ system, code }) => `${system ?? ''}|${code ?? ''}`)
      .join(',');
  }

  // Changed only for lab reports
  public set code(value: string | null | undefined) {
    this.model = {
      ...this.model,
      ...(this.category === 'laboratory' && {
        code: value
          ? {
              coding: [
                {
                  code: value,
                  display: LAB_REPORT_NAME_MAP[value],
                  system: DiagnosticReportRecord.VITALL_SYSTEM,
                },
              ],
              text: LAB_REPORT_NAME_MAP[value],
            }
          : {},
      }),
    };
  }

  public get testDate() {
    return this.model.effectiveDateTime;
  }

  public set testDate(value: string | null | undefined) {
    this.model = {
      ...this.model,
      effectiveDateTime: value,
    };
  }

  public get studies() {
    const { imagingStudy } = this.model;
    const studies = imagingStudy ?? [];
    return studies;
  }

  /**
   * (Imaging Report Only) Test name
   */
  public get testName() {
    const extension = this.findExtension(IMAGING_AND_TESTS_TEST_NAME);
    return extension?.valueString;
  }

  public set testName(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: IMAGING_AND_TESTS_TEST_NAME,
        valueString: value,
      });
    } else {
      this.removeExtension(IMAGING_AND_TESTS_TEST_NAME);
    }
  }

  /**
   * (Imaging Report Only) Reported Date
   */
  public get reportedDate() {
    const extension = this.findExtension(IMAGING_AND_TESTS_REPORTED_DATE);
    return extension?.valueDateTime;
  }

  public set reportedDate(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: IMAGING_AND_TESTS_REPORTED_DATE,
        valueDateTime: value,
      });
    } else {
      this.removeExtension(IMAGING_AND_TESTS_REPORTED_DATE);
    }
  }

  /**
   * (Imaging Report Only) History
   */
  public get history() {
    const extension = this.findExtension(IMAGING_AND_TESTS_HISTORY);
    return extension?.valueString;
  }

  public set history(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: IMAGING_AND_TESTS_HISTORY,
        valueString: value,
      });
    } else {
      this.removeExtension(IMAGING_AND_TESTS_HISTORY);
    }
  }

  /**
   * (Imaging Report Only) Technique
   */
  public get technique() {
    const extension = this.findExtension(IMAGING_AND_TESTS_TECHNIQUE);
    return extension?.valueString;
  }

  public set technique(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: IMAGING_AND_TESTS_TECHNIQUE,
        valueString: value,
      });
    } else {
      this.removeExtension(IMAGING_AND_TESTS_TECHNIQUE);
    }
  }

  /**
   * (Imaging Report Only) Findings
   */
  public get findings() {
    const extension = this.findExtension(IMAGING_AND_TESTS_FINDINGS);
    return extension?.valueString;
  }

  public set findings(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: IMAGING_AND_TESTS_FINDINGS,
        valueString: value,
      });
    } else {
      this.removeExtension(IMAGING_AND_TESTS_FINDINGS);
    }
  }

  /**
   * (Imaging Report Only) Recommendations
   */
  public get recommendations() {
    const extension = this.findExtension(IMAGING_AND_TESTS_RECOMMENDATIONS);
    return extension?.valueString;
  }

  public set recommendations(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: IMAGING_AND_TESTS_RECOMMENDATIONS,
        valueString: value,
      });
    } else {
      this.removeExtension(IMAGING_AND_TESTS_RECOMMENDATIONS);
    }
  }

  /**
   * (Imaging Report Only) Available images
   */
  public get imagesAvailable() {
    const extension = this.findExtension(IMAGING_AND_TESTS_AVAILABLE_IMAGES);
    return extension?.valueBoolean ?? true;
  }

  public set imagesAvailable(value: boolean) {
    if (typeof value === 'boolean') {
      this.addOrReplaceExtension({
        url: IMAGING_AND_TESTS_AVAILABLE_IMAGES,
        valueBoolean: value,
      });
    }
  }

  /**
   * (Lab Report) Conclusion
   * (Imaging Report) Impression
   */
  public get details() {
    return this.model.conclusion;
  }

  public set details(value: string | null | undefined) {
    this.model = {
      ...this.model,
      conclusion: value ?? null,
    };
  }

  /**
   * (Lab Report Only) Test Name
   */
  public get reportTestName() {
    const extension = this.findExtension(LAB_REPORT_TEST_NAME);
    return extension?.valueString;
  }

  public set reportTestName(value: string | null | undefined) {
    if (value) {
      this.addOrReplaceExtension({
        url: LAB_REPORT_TEST_NAME,
        valueString: value,
      });
    } else {
      this.removeExtension(LAB_REPORT_TEST_NAME);
    }
  }

  /**
   * (Lab Report Only) Lab Observations
   */
  public get results() {
    return this.model.resultResources?.map(
      (r) => new LabResultObservationRecord(r)
    );
  }

  public get resultCount() {
    return this.model.result?.length ?? 0;
  }

  public get imagingStudies() {
    return this.model.imagingStudyResources?.map(
      (r) => new ImagingStudyRecord(r)
    );
  }

  public get hasImages() {
    return Boolean(
      this.model.imagingStudy?.length ||
      this.model.imagingStudyResources?.length
    );
  }

  public get patient() {
    return this.model.subject?.reference;
  }

  public hasCode(codeId: string) {
    return this.model.code.coding?.some((c) => c.code === codeId);
  }

  public hasCategory(codeId: string) {
    return this.model.category?.some((c) =>
      c.coding?.some((o) => o.code === codeId)
    );
  }

  public getTestDate() {
    return getFhirDate(this.model.effectiveDateTime);
  }

  public getTestDateStr() {
    return formatFhirDate(this.model.effectiveDateTime, {
      adjustTz: false,
    });
  }

  public getReportedDate() {
    return getFhirDate(this.reportedDate);
  }

  public getReportedDateStr() {
    return formatFhirDate(this.reportedDate, {
      adjustTz: false,
    });
  }

  public addImagingStudies(studies: Reference[]) {
    const existingIds = this.model.imagingStudy?.map((i) => i?.reference);
    const filtered = studies.filter(
      ({ reference }) => !existingIds?.includes(reference)
    );
    this.model = {
      ...this.model,
      imagingStudy: [...(this.model.imagingStudy ?? []), ...filtered],
    };
  }

  public getModelInput(values?: DiagnosticReportValues): DiagnosticReportInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      if (this.category === 'laboratory') {
        this.reportTestName = values.title;
      } else if (this.category === 'imaging-and-tests') {
        this.testName = values.title;
      }
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    delete cleaned.encounter;
    delete cleaned.encounterResource;
    delete cleaned.imagingStudyResources;
    delete cleaned.resultResources;
    return {
      ...cleaned,
      status: 'final',
    };
  }
}
